// redux/slices/expensesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { resetStore } from './resetStore';
import { getServerUrl } from '../../Utils/serverSelector';

// 🔐 централизованный JWT-клиент
import { apiRequest, getAuthHeaders } from '../../Utils/apiClient';
import { clearJwtToken } from '../../Utils/authStorage';

/* ===========================
   helpers
   =========================== */

// сервер иногда возвращает { expense } или сам объект
const normalizeExpense = (data) => (data && (data.expense || data)) || null;

// убираем Content-Type — пусть браузер сам выставит boundary для multipart
const stripContentType = (h = {}) => {
  const copy = { ...h };
  delete copy['Content-Type'];
  delete copy['content-type'];
  return copy;
};

// универсальная сборка FormData для веб
// ожидает payload вида:
// { uid, date, categoryId, supplier, paymentMethod, price, quantity, totalCost,
//   description, comments, removeAttachment, file? }
const toFormData = (expense = {}) => {
  const fd = new FormData();

  // файл: File/Blob (веб)
  if (expense.file instanceof File || expense.file instanceof Blob) {
    const name = expense.file.name || 'attachment.jpg';
    const type = expense.file.type || 'image/jpeg';
    fd.append('file', expense.file, name);
  }

  // сериализуем примитивы как строки; объекты — JSON
  Object.entries(expense).forEach(([k, v]) => {
    if (k === 'file') return; // уже добавили
    if (v === undefined || v === null) return;
    const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
    fd.append(k, val);
  });

  return fd;
};

/* ===========================
   thunks
   =========================== */

// GET /expenses/:uid  (PRIVATE, JWT)
export const fetchExpenses = createAsyncThunk(
  'expenses/fetchExpenses',
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');

      const res = await apiRequest(
        'GET',
        `/expenses/${encodeURIComponent(uid)}`,
        null,
        {
          // позволяем сами обработать 404/ошибки
          validateStatus: () => true,
        }
      );

      if (res.status === 404) return [];
      if (res.status !== 200) {
        throw new Error(`Ошибка сервера: ${res.status}`);
      }

      return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
      }
      return rejectWithValue(
        e?.response?.data || e?.message || 'Ошибка при загрузке расходов'
      );
    }
  }
);

// POST /expenses  (multipart, PRIVATE, JWT + cookies)
export const addExpense = createAsyncThunk(
  'expenses/addExpense',
  async (expense, { rejectWithValue }) => {
    try {
      if (!expense?.uid) throw new Error('UID не передан');

      const form = toFormData(expense);

      const authHeaders = stripContentType(getAuthHeaders());

      const res = await axios.post(`${getServerUrl()}/expenses`, form, {
        headers: authHeaders,
        withCredentials: true,            // 👈 ОБЯЗАТЕЛЬНО
        validateStatus: () => true,
      });

      if (res.status !== 201 && res.status !== 200) {
        throw new Error(`Ошибка сервера: ${res.status}`);
      }

      const parsed = normalizeExpense(res.data);
      if (!parsed) throw new Error('Некорректный ответ сервера');
      return parsed;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
      }
      return rejectWithValue(
        e?.response?.data || e?.message || 'Ошибка при добавлении расхода'
      );
    }
  }
);

// PUT /expenses/:id  (multipart, PRIVATE, JWT + cookies)
export const updateExpense = createAsyncThunk(
  'expenses/updateExpense',
  async ({ id, expense }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error('ID не передан');
      if (!expense?.uid) throw new Error('UID не передан');

      const form = toFormData(expense);
      const authHeaders = stripContentType(getAuthHeaders());

      const res = await axios.put(`${getServerUrl()}/expenses/${id}`, form, {
        headers: authHeaders,
        withCredentials: true,            // 👈 ТОЖЕ ВАЖНО
        validateStatus: () => true,
      });

      if (res.status !== 200) {
        throw new Error(`Ошибка сервера: ${res.status}`);
      }

      const parsed = normalizeExpense(res.data);
      if (!parsed) throw new Error('Некорректный ответ сервера');
      return parsed;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
      }
      return rejectWithValue(
        e?.response?.data || e?.message || 'Ошибка при обновлении расхода'
      );
    }
  }
);

// DELETE /expenses/:id  (PRIVATE, JWT)
export const deleteExpense = createAsyncThunk(
  "expenses/deleteExpense",
  async (id, { rejectWithValue }) => {
    try {
      if (id === undefined || id === null || id === "") {
        throw new Error("ID расхода не передан");
      }

      const res = await apiRequest(
        "DELETE",
        `/expenses/${encodeURIComponent(String(id))}`
      );

      if (res.status !== 200 && res.status !== 204) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Ошибка удаления расхода (HTTP ${res.status})`
        );
      }

      return String(id);
    } catch (e) {
      const status = e?.response?.status;

      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch { }
      }

      return rejectWithValue(
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.response?.data ||
        e?.message ||
        "Ошибка при удалении расхода"
      );
    }
  }
);

/* ===========================
   slice
   =========================== */

const initialState = {
  expenses: [],
  status: 'idle',   // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

const expensesSlice = createSlice({
  name: 'expenses',
  initialState,
  reducers: {
    clearExpensesError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchExpenses.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.expenses = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload || action.error?.message || 'Fetch failed';
      })

      // add
      .addCase(addExpense.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(addExpense.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload) state.expenses.push(action.payload);
      })
      .addCase(addExpense.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload || action.error?.message || 'Add failed';
      })

      // update
      .addCase(updateExpense.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const updated = action.payload;
        const idx = state.expenses.findIndex((e) => e.id === updated?.id);
        if (idx !== -1) state.expenses[idx] = updated;
      })
      .addCase(updateExpense.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload || action.error?.message || 'Update failed';
      })

      // delete
      .addCase(deleteExpense.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;

        state.expenses = state.expenses.filter(
          (expense) =>
            String(expense.id) !== String(action.payload)
        );
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload || action.error?.message || 'Delete failed';
      })

      // reset
      .addCase(resetStore, () => initialState);
  },
});

export const { clearExpensesError } = expensesSlice.actions;
export default expensesSlice.reducer;
